<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { useOverlay } from './overlay';

/**
 * The confirm surface, and only that. Centred and interrupting, because
 * interrupting is the entire point.
 *
 * **Not dismissed by its backdrop**, where a drawer is. A stray click should
 * not answer a question about deleting something.
 */
const props = withDefaults(
	defineProps<{
		open: boolean;
		title: string;
		/**
		 * Where the action cannot be undone: the name of the thing, typed out.
		 * This is what stands in for step-up authentication, which `010` rules
		 * out as impractical in a passwordless system and no help against the
		 * likelier harm — a legitimate user making a mistake.
		 */
		confirmPhrase?: string | undefined;
		confirmLabel?: string | undefined;
		busy?: boolean | undefined;
	}>(),
	{ confirmLabel: 'Confirm', busy: false },
);
const emit = defineEmits<{ close: []; confirm: [] }>();

const panel = ref<HTMLElement>();
const titleId = useId();
const typed = ref('');

// Reopening must not inherit the last answer, or a second deletion is one
// click where the first took typing.
watch(
	() => props.open,
	(open) => {
		if (open) typed.value = '';
	},
);

const satisfied = computed(
	() => props.confirmPhrase === undefined || typed.value.trim() === props.confirmPhrase,
);

useOverlay({
	open: () => props.open,
	container: panel,
	onClose: () => {
		emit('close');
	},
	closeOnBackdrop: false,
});
</script>

<template>
	<Teleport to="body">
		<div
			v-if="open"
			class="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
		>
			<div
				ref="panel"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				tabindex="-1"
				class="bg-surface border-border w-full max-w-md rounded-lg border p-5 shadow-xl focus:outline-none"
			>
				<h2 :id="titleId" class="font-medium">{{ title }}</h2>

				<!-- Destructive actions show their consequences before asking. -->
				<div class="text-muted mt-2 text-sm"><slot /></div>

				<div v-if="confirmPhrase" class="mt-4">
					<label :for="`${titleId}-phrase`" class="text-sm">
						Type <code class="text-fg font-medium">{{ confirmPhrase }}</code> to confirm
					</label>
					<input
						:id="`${titleId}-phrase`"
						v-model="typed"
						type="text"
						autocomplete="off"
						class="border-border bg-surface mt-1.5 w-full rounded-md border px-3 py-2 text-sm"
					/>
				</div>

				<div class="mt-5 flex justify-end gap-2">
					<button
						type="button"
						class="border-border hover:bg-border/40 rounded-md border px-3 py-2 text-sm font-medium"
						@click="emit('close')"
					>
						Cancel
					</button>
					<button
						type="button"
						class="bg-danger text-danger-fg rounded-md px-3 py-2 text-sm font-medium disabled:cursor-default disabled:opacity-60"
						:disabled="!satisfied || busy"
						@click="emit('confirm')"
					>
						{{ confirmLabel }}
					</button>
				</div>
			</div>
		</div>
	</Teleport>
</template>
